import {attr, controller, target, targets} from '@github/catalyst'
import {currencyFormatter, getUnitData} from './utils'
import type {UnitData} from './utils'

@controller
export class ActionsCalculatorElement extends HTMLElement {
  @targets declare runnerTypes: HTMLInputElement[]
  get selectedRunnerType(): HTMLInputElement | undefined {
    return this.runnerTypes.find(opt => opt.checked)
  }
  get runnerTypeLabel() {
    return (this.selectedRunnerType && this.selectedRunnerType.getAttribute('data-label')) || ''
  }
  get runnerType() {
    return (this.selectedRunnerType && this.selectedRunnerType.value) || ''
  }

  @targets declare osTypes: HTMLInputElement[]
  get selectedOsType(): HTMLInputElement | undefined {
    return this.osTypes.find(opt => opt.checked)
  }
  get osTypeLabel() {
    return (this.selectedOsType && this.selectedOsType.getAttribute('data-label')) || ''
  }
  get osType() {
    return (this.selectedOsType && this.selectedOsType.value) || ''
  }

  @target declare jobCountInput: HTMLInputElement
  get jobCount() {
    return Number(this.jobCountInput.value) || 0
  }

  @target declare jobDurationInput: HTMLInputElement
  get jobDuration() {
    return Number(this.jobDurationInput.value) || 0
  }

  get hasValidInputs() {
    if (!(this.jobCount && this.jobDuration && this.selectedRunnerType)) {
      return false
    }

    if (!(this.runnerType === 'action_runners_self_hosted' || this.osType)) {
      return false
    }

    const runnerCostKey = this.runnerType === 'action_runners_self_hosted' ? this.runnerType : this.osType
    if (!this.unit_data.hasOwnProperty(runnerCostKey)) {
      return false
    }

    return true
  }

  @targets declare runners: ActionRunnerElement[]
  @target declare addRunnerButton: HTMLElement
  @target declare osTypesContainer: HTMLElement
  @target declare runnersContainer: HTMLElement
  @target declare runnersList: HTMLElement
  @target declare runnerTemplate: HTMLTemplateElement
  @target declare costPerMonthDisplay: HTMLElement

  @attr costPerMonth = 0
  @attr active = false
  @attr runnerCount = 0

  declare unit_data: UnitData

  connectedCallback() {
    this.unit_data = getUnitData()
  }

  runnerTypeChanged() {
    this.validateInputs()
    this.osTypesContainer.hidden = this.runnerType !== 'action_runners_standard'
  }

  validateInputs() {
    if (this.hasValidInputs) {
      this.addRunnerButton.classList.remove('grayscale')
      this.addRunnerButton.removeAttribute('disabled')
    } else {
      this.addRunnerButton.classList.add('grayscale')
      this.addRunnerButton.setAttribute('disabled', 'disabled')
    }
  }

  addRunnerButtonClicked() {
    if (this.hasValidInputs) {
      const runnerCostKey = this.runnerType === 'action_runners_self_hosted' ? this.runnerType : this.osType
      const runnerTypeCost = this.unit_data[runnerCostKey]!.value
      const cost = this.calculateRunnerCost(runnerTypeCost, this.jobCount, this.jobDuration)

      this.appendRunner(cost, this.jobCount, this.jobDuration, this.runnerTypeLabel, this.osTypeLabel)
      this.updateMonthlyCost()
      this.resetForm()
    }
  }

  resetForm() {
    this.jobCountInput.value = ''
    this.jobCountInput.dispatchEvent(new Event('input'))

    this.jobDurationInput.value = ''
    this.jobDurationInput.dispatchEvent(new Event('input'))

    for (const runnerOption of this.runnerTypes) {
      runnerOption.checked = false
      runnerOption.dispatchEvent(new Event('change'))
    }

    for (const osOption of this.osTypes) {
      osOption.checked = false
      osOption.dispatchEvent(new Event('change'))
    }
  }

  appendRunner(cost: number, jobCount: number, jobDuration: number, runnerTypeLabel: string, osTypeLabel?: string) {
    const newRunner = this.runnerTemplate.content.firstElementChild!.cloneNode(true) as ActionRunnerElement
    newRunner.cost = cost
    newRunner.jobCount = jobCount
    newRunner.jobDuration = jobDuration
    newRunner.runnerTypeLabel = runnerTypeLabel
    if (osTypeLabel) {
      newRunner.osTypeLabel = osTypeLabel
    }
    this.runnersList.prepend(newRunner)
    this.runnersContainer.hidden = false
    this.runnerCount = this.runners.length
  }

  calculateRunnerCost(runnerTypeCost: number, jobCount: number, jobDuration: number) {
    return runnerTypeCost * jobCount * jobDuration * 30
  }

  updateMonthlyCost() {
    this.costPerMonth = this.runners.reduce((accum, runner) => accum + runner.cost, 0)
  }

  resetButtonClicked(event: PointerEvent) {
    event.preventDefault()
    this.resetForm()
    this.runnersList.textContent = ''
    this.runnersContainer.hidden = true
    this.costPerMonth = 0
  }

  getSummaryForCSV() {
    return this.runners.map(runner => runner.getSummaryForCSV())
  }

  static observedAttributes = ['data-cost-per-month', 'data-runner-count', 'data-active']
  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) {
      return
    }

    if (name === 'data-cost-per-month' || name === 'data-active') {
      this.costPerMonthDisplay.textContent = currencyFormatter.format(this.costPerMonth / 100)
      document.dispatchEvent(
        new CustomEvent('subtotalChanged', {
          detail: {sectionName: 'actionsTotal', amount: this.costPerMonth, active: this.active},
        }),
      )
    }

    if (name === 'data-cost-per-month' || name === 'data-runner-count') {
      document.dispatchEvent(
        new CustomEvent('formDataUpdated', {
          detail: {
            sectionName: 'actions',
            resetEnabled: Boolean(this.runnerCount) || Boolean(this.costPerMonth),
          },
        }),
      )
    }
  }
}

@controller
class ActionRunnerElement extends HTMLElement {
  @attr declare jobCount: number
  @attr declare jobDuration: number
  @attr declare runnerTypeLabel: string
  @attr declare osTypeLabel: string
  @attr declare cost: number
  get formattedCost() {
    return this.cost === 0 ? 'Free' : currencyFormatter.format(this.cost / 100)
  }

  @target declare jobCountDisplay: HTMLElement
  @target declare jobDurationisplay: HTMLElement
  @target declare runnerTypeDisplay: HTMLElement
  @target declare osTypeDisplay: HTMLElement
  @target declare costDisplay: HTMLElement

  connectedCallback() {
    this.jobCountDisplay.textContent = String(this.jobCount)
    this.jobDurationisplay.textContent = String(this.jobDuration)
    this.runnerTypeDisplay.textContent = String(this.runnerTypeLabel)
    this.osTypeDisplay.textContent = String(this.osTypeLabel)
    this.costDisplay.textContent = this.formattedCost

    this.addEventListener('transitionend', () => {
      const actionsCalculator = this.closest('actions-calculator') as ActionsCalculatorElement
      this.remove()
      actionsCalculator.runnersContainer.hidden = !actionsCalculator.querySelectorAll('action-runner').length
    })
  }

  calculateCost() {
    this.cost = 0
  }

  removeSelf() {
    this.classList.add('action-runner--deleted')
    const actionsCalculator = document.querySelector('actions-calculator') as ActionsCalculatorElement
    actionsCalculator.updateMonthlyCost()
  }

  getSummaryForCSV() {
    // headers: ['Service', 'Configuration Summary', 'Notes', 'Cost Per Month']

    return [
      'Actions',
      `"Jobs Per Day: ${this.jobCount}, Avg. Job Duration (min): ${this.jobDuration}, Runner type: ${this.runnerTypeLabel}, OS type: ${this.osTypeLabel}"`,
      '',
      `"${currencyFormatter.format(this.cost / 100)}"`,
    ]
  }
}
